import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import FileDropZone from '../components/FileDropZone';

// 고객사/협력사 배지 색상 — 계약관리(Contracts.js) tabStyleOf처럼 고정 팔레트 사용(카테고리가
// 딱 2개뿐이라 해시 대신 직접 지정)
const CATEGORY_STYLE = {
  '고객사': { color:'#185FA5', bg:'#E6F1FB' },
  '협력사': { color:'#0F6E56', bg:'#E1F5EE' },
};
const CATEGORIES = ['고객사', '협력사'];
const styleOf = (cat) => CATEGORY_STYLE[cat] || { color:'#666', bg:'#f0f0f0' };

// 사업자구분(법인/개인 등) 배지 색상
const ENTITY_TYPE_STYLE = {
  '법인':   { color:'#7C3AED', bg:'#F3E8FF' },
  '개인':   { color:'#B45309', bg:'#FEF3C7' },
  '사단법인': { color:'#0E7490', bg:'#E0F7FA' },
  '재단법인': { color:'#BE185D', bg:'#FCE7F3' },
  '협동조합': { color:'#15803D', bg:'#DCFCE7' },
};
const ENTITY_TYPES = ['법인', '개인', '사단법인', '재단법인', '협동조합', '기타'];
const entityStyleOf = (t) => ENTITY_TYPE_STYLE[t] || { color:'#666', bg:'#f0f0f0' };

// 목록 컬럼명 클릭 정렬(오름/내림차순 토글) 공용 정렬 함수 — 계약관리(Contracts.js) 등에서
// 쓰는 것과 동일한 패턴. 숫자면 숫자비교, 아니면 문자열비교(한글 로케일 기준).
function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let va = a ? a[sortKey] : null;
    let vb = b ? b[sortKey] : null;
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    let cmp;
    const na = Number(va), nb = Number(vb);
    if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
    else cmp = String(va).localeCompare(String(vb), 'ko');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// 목록 헤더 <span> — 컬럼명을 클릭할 때마다 오름차순/내림차순이 토글된다(활성 컬럼만 색상 강조).
function SortableTh({ label, sortKey, activeKey, dir, onClick, style }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)}
      title={active ? (dir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      style={{ ...style, cursor:'pointer', userSelect:'none', color: active ? '#185FA5' : (style?.color), fontWeight: active ? 700 : (style?.fontWeight) }}>
      {label}
    </span>
  );
}

// 첨부파일(사업자등록증/통장사본) 다운로드 — 인증된 axios 인스턴스(API)로 blob을 받아 강제
// 다운로드한다. 예전에는 순수 <a href={...}> 태그로 백엔드 다운로드 URL을 직접 열었는데, 이
// 방식은 로그인 토큰(Authorization 헤더)이 실리지 않는 일반 브라우저 이동이라 서버가 401을
// 돌려줘 "로그인이 필요합니다" 메시지가 뜨고 다운로드가 되지 않았다(인증관리 Certs.js에서
// 동일한 문제를 이미 이 방식으로 해결한 적이 있어 그 패턴을 그대로 따른다).
function downloadCompanyFile(fileName, oriName, onError) {
  API.get(`/companies/download/${encodeURIComponent(fileName)}`, { responseType: 'blob' })
    .then(res => {
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = oriName || fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      logDownload('업체관리', oriName || fileName);
    })
    .catch(err => {
      console.error('다운로드 실패:', err.message);
      if (onError) onError();
    });
}

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editData, setEditData]   = useState(null);
  const [showAutoRegister, setShowAutoRegister] = useState(false);
  const [msg, setMsg] = useState('');

  const perm = usePermissions();
  const { hasDetail, canWrite: canWriteMenu } = perm;
  const canDownload = hasDetail('company-management', 'download_allowed');
  const canDelete    = hasDetail('company-management', 'delete_allowed');
  const canWrite      = canWriteMenu('company-management');

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  const load = () => API.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // 상태바(최신 업데이트 현황)에서 업체관리 항목을 클릭해 들어온 경우 그 항목의 상세 모달을 바로 연다.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const ao = location.state?.activityOpen;
    if (ao && companies.length) {
      const found = companies.find(c => String(c.id) === String(ao.itemId));
      if (found) setSelected(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, companies]); // eslint-disable-line

  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  // ── 탭메뉴(전체 + 고객사 + 협력사) ──────────────────────────
  const [activeTab, setActiveTab] = useState('전체');

  // ── 검색 ──────────────────────────────────────────────
  const [search, setSearch] = useState('');

  // ── 목록 컬럼명 클릭 정렬 — null(기본)이면 기존과 동일하게 등록 순서 그대로 유지된다.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  // ── 페이지당 표시개수 ────────────────────────────────────
  const [pageSize, setPageSize] = useState('all');
  const [page, setPage] = useState(1);

  // ── 전체선택 / 일괄 이동·삭제 ────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [moveTarget, setMoveTarget] = useState('협력사');

  const filtered = companies.filter(c => {
    const matchTab = activeTab === '전체' || c.company_category === activeTab;
    const matchSearch = !search ||
      (c.company_name || '').includes(search) ||
      (c.ceo_name || '').includes(search) ||
      (c.biz_reg_no || '').includes(search) ||
      (c.manager_name || '').includes(search);
    return matchTab && matchSearch;
  });

  useEffect(() => { setPage(1); setSelectMode(false); setSelectedIds(new Set()); }, [activeTab, search, pageSize, sortKey, sortDir]);

  const sortedFiltered = sortRows(filtered, sortKey, sortDir);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const pagedList  = pageSize === 'all' ? sortedFiltered : sortedFiltered.slice((page - 1) * pageSize, page * pageSize);

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete(`/companies/${id}`);
    setSelected(null);
    setMsg('삭제 완료!');
    load();
  };

  const handleEdit = (company) => {
    setEditData(company);
    setShowForm(true);
    setSelected(null);
  };

  // 상세보기 우측상단 "고객사로 분류"/"협력사로 분류" 버튼 — 파일 등은 그대로 두고
  // company_category만 바꿔서 PUT한다(다른 필드가 비어서 전송되면 서버에서 null로 덮어써지므로
  // selected에 담긴 기존 값 전부를 함께 실어 보낸다).
  const handleReclassify = async (company, newCategory) => {
    if (company.company_category === newCategory) return;
    if (!window.confirm(`"${company.company_name}"을(를) ${newCategory}(으)로 분류하시겠습니까?`)) return;
    const fd = new FormData();
    const fields = [
      'entity_type', 'company_name', 'ceo_name', 'biz_reg_no', 'biz_address', 'biz_type', 'biz_item',
      'manager_name', 'manager_contact', 'manager_email', 'bank_name', 'account_holder', 'account_number', 'note',
    ];
    fd.append('company_category', newCategory);
    fields.forEach(k => fd.append(k, company[k] ?? ''));
    try {
      await API.put(`/companies/${company.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSelected(null);
      setMsg(`${newCategory}(으)로 분류되었습니다.`);
      load();
    } catch (err) {
      alert(err.response?.data?.message || '분류 변경에 실패했습니다.');
    }
  };

  // 전체선택 버튼 — 누르면 선택모드로 전환하면서 현재 검색/탭 조건에 맞는 업체를 모두 선택하고,
  // 다시 누르면(이미 선택모드) 선택을 해제하고 모드를 빠져나간다.
  const handleToggleSelectAll = () => {
    if (selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    } else {
      setSelectMode(true);
      setSelectedIds(new Set(filtered.map(c => c.id)));
    }
  };

  const toggleRowSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allFilteredIds = filtered.map(c => c.id);
  const isHeaderChecked = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.has(id));
  const handleHeaderCheckbox = () => {
    setSelectedIds(isHeaderChecked ? new Set() : new Set(allFilteredIds));
  };

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.size}개 업체를 삭제하시겠습니까?`)) return;
    try {
      await Promise.all([...selectedIds].map(id => API.delete(`/companies/${id}`)));
      setMsg(`${selectedIds.size}개 업체를 삭제했습니다.`);
      exitSelectMode();
      load();
    } catch (err) {
      alert('일부 업체 삭제에 실패했습니다.');
      load();
    }
  };

  // 선택한 업체들을 moveTarget(고객사/협력사)으로 일괄 이동한다 — handleReclassify와 동일하게
  // 다른 필드가 비어서 전송되면 서버에서 null로 덮어써지므로 기존 값 전부를 함께 실어 보낸다.
  const handleBulkMove = async () => {
    if (selectedIds.size === 0) return;
    const targets = companies.filter(c => selectedIds.has(c.id) && c.company_category !== moveTarget);
    if (targets.length === 0) { alert(`선택한 업체가 이미 모두 ${moveTarget}입니다.`); return; }
    if (!window.confirm(`선택한 업체 중 ${targets.length}개를 ${moveTarget}(으)로 이동하시겠습니까?`)) return;
    const fields = ['entity_type', 'company_name', 'ceo_name', 'biz_reg_no', 'biz_address', 'biz_type', 'biz_item',
      'manager_name', 'manager_contact', 'manager_email', 'bank_name', 'account_holder', 'account_number', 'note'];
    try {
      await Promise.all(targets.map(c => {
        const fd = new FormData();
        fd.append('company_category', moveTarget);
        fields.forEach(k => fd.append(k, c[k] ?? ''));
        return API.put(`/companies/${c.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }));
      setMsg(`${targets.length}개 업체를 ${moveTarget}(으)로 이동했습니다.`);
      exitSelectMode();
      load();
    } catch (err) {
      alert('일부 업체 이동에 실패했습니다.');
      load();
    }
  };

  // ── 인쇄 ──────────────────────────────────────────────
  const handlePrint = () => {
    const rows = filtered.map(c => `
      <tr>
        <td>${c.company_category}</td>
        <td>${c.company_name || '-'}</td>
        <td>${c.ceo_name || '-'}</td>
        <td>${c.biz_reg_no || '-'}</td>
        <td>${c.manager_name || '-'}</td>
        <td>${c.manager_contact || '-'}</td>
        <td>${c.created_at?.slice(0, 10) || '-'}</td>
      </tr>`).join('');
    const html = `
      <h2 style="text-align:center;margin-bottom:12px;">🏢 업체관리 (${activeTab})</h2>
      <table border="1" cellpadding="6" cellspacing="0" style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f0f0f0;">
          <th>구분</th><th>회사명</th><th>대표자</th><th>사업자등록번호</th><th>담당자</th><th>담당자 연락처</th><th>등록일</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    const now = new Date();
    const printDT = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printBy = printedByLabel(user);
    const footerHtml = `<div style="display:flex;justify-content:space-between;margin-top:14px;padding-top:8px;border-top:1px solid #ddd;font-size:11px;color:#555;">
        <span>출력일시: ${printDT}</span><span>출력자: ${printBy}</span>
      </div>`;
    const pw = window.open('', '_blank', 'width=1200,height=900');
    pw.document.write(`<!DOCTYPE html><html><head><title>업체관리</title>
      <style>@page{size:A4 landscape;margin:0}body{font-family:sans-serif;margin:0;padding:10mm;box-sizing:border-box}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:5px 8px;text-align:center}</style>
      </head><body>${html}${footerHtml}</body></html>`);
    pw.document.close(); pw.focus();
    logPrint('업체관리', `업체목록(${activeTab})`);
    setTimeout(() => { pw.print(); pw.close(); }, 500);
  };

  // ── 엑셀 내보내기 ──────────────────────────────────────
  const handleExport = () => {
    const rows = filtered.map(c => ({
      '구분': c.company_category,
      '회사명': c.company_name || '',
      '대표자': c.ceo_name || '',
      '사업자등록번호': c.biz_reg_no || '',
      '사업장소재지': c.biz_address || '',
      '업태': c.biz_type || '',
      '종목': c.biz_item || '',
      '담당자': c.manager_name || '',
      '담당자 연락처': c.manager_contact || '',
      '담당자 이메일': c.manager_email || '',
      '은행명': c.bank_name || '',
      '예금주': c.account_holder || '',
      '계좌번호': c.account_number || '',
      '등록일': c.created_at?.slice(0, 10) || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:8},{wch:18},{wch:10},{wch:16},{wch:24},{wch:12},{wch:12},{wch:10},{wch:14},{wch:20},{wch:12},{wch:12},{wch:18},{wch:12}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '업체목록');
    const filename = `업체관리_${activeTab}_${new Date().toISOString().slice(0,10)}.xlsx`;
    XLSX.writeFile(wb, filename);
    logDownload('업체관리', filename);
  };

  return (
    <div style={{ padding:24, maxWidth:1690 }}>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🏢 업체관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>고객사·협력사를 사업자등록증 기반으로 등록하고 관리합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={handlePrint}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          <button onClick={handleExport}
            style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📥 내보내기
          </button>
          {canWrite && (
            <button onClick={() => setShowAutoRegister(true)}
              style={{ background:'#fff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🤖 자동등록
            </button>
          )}
          {canWrite && (
            <button onClick={() => { setShowForm(true); setEditData(null); }}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              ✍️ 업체등록
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 탭메뉴(전체/고객사/협력사) + 우측 검색/표시개수 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:10, flexWrap:'wrap', gap:10, borderBottom:'1px solid #e5e7eb' }}>
        <div style={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
          {['전체', ...CATEGORIES].map(name => {
            const count = name === '전체' ? companies.length : companies.filter(c => c.company_category === name).length;
            const active = activeTab === name;
            const style = name === '전체' ? { color:'#475569', bg:'#E2E8F0' } : styleOf(name);
            return (
              <button key={name} onClick={() => setActiveTab(name)}
                style={{ padding:'10px 18px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                  borderBottom: active ? '2px solid #185FA5' : '2px solid #e5e7eb',
                  background:'none', color: active ? '#185FA5' : '#888' }}>
                {name}
                {count > 0 && (
                  <span style={{ fontSize:11, marginLeft:6, background: active ? '#185FA5' : '#e5e7eb',
                    color: active ? '#fff' : '#888', padding:'1px 7px', borderRadius:20 }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          {selectMode && (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:13, color:'#1e3a8a', fontWeight:600, whiteSpace:'nowrap' }}>{selectedIds.size}개 선택됨</span>
              {canWrite && (
                <>
                  <select value={moveTarget} onChange={e => setMoveTarget(e.target.value)}
                    style={{ padding:'7px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, background:'#fff', cursor:'pointer' }}>
                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                  <button onClick={handleBulkMove} disabled={selectedIds.size === 0}
                    style={{ padding:'7px 14px', background: selectedIds.size ? '#185FA5' : '#aaa', color:'#fff', border:'none',
                      borderRadius:8, fontSize:13, fontWeight:600, cursor: selectedIds.size ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
                    📤 이동
                  </button>
                </>
              )}
              {canDelete && (
                <button onClick={handleBulkDelete} disabled={selectedIds.size === 0}
                  style={{ padding:'7px 14px', background: selectedIds.size ? '#dc2626' : '#aaa', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600, cursor: selectedIds.size ? 'pointer' : 'not-allowed', whiteSpace:'nowrap' }}>
                  🗑️ 삭제
                </button>
              )}
            </div>
          )}

          {(canWrite || canDelete) && (
            <button onClick={handleToggleSelectAll}
              style={{ padding:'8px 14px', border: selectMode ? '1px solid #185FA5' : '1px solid #ddd', borderRadius:8,
                background: selectMode ? '#185FA5' : '#fff', color: selectMode ? '#fff' : '#555',
                fontSize:14, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
              ☑️ 전체선택
            </button>
          )}

          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 회사명, 대표자, 사업자번호, 담당자 검색..."
            style={{ padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, width:260, boxSizing:'border-box' }} />

          <select value={pageSize} onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555',
              outline:'none', cursor:'pointer', background:'#fff' }}>
            <option value="10">10개씩</option>
            <option value="30">30개씩</option>
            <option value="50">50개씩</option>
            <option value="100">100개씩</option>
            <option value="all">모두보기</option>
          </select>
        </div>
      </div>

      {/* 목록 */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa', fontSize:14 }}>
          등록된 업체가 없습니다.<br />
          {canWrite && <span style={{ fontSize:13 }}>상단 "✍️ 업체등록" 버튼을 눌러 추가하세요.</span>}
        </div>
      ) : (
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', overflowX:'auto', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'grid', gridTemplateColumns: selectMode ? '36px 90px 1fr 110px 150px 350px 90px 90px 210px 100px 130px 110px' : '90px 1fr 110px 150px 350px 90px 90px 210px 100px 130px 110px',
            background:'#f8fafc', padding:'10px 10px', fontSize:14, fontWeight:600,
            color:'#555', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
            {selectMode && (
              <span onClick={handleHeaderCheckbox} style={{ cursor:'pointer' }}>
                <input type="checkbox" checked={isHeaderChecked} onChange={handleHeaderCheckbox}
                  onClick={e => e.stopPropagation()} style={{ cursor:'pointer' }} />
              </span>
            )}
            {/* 목록 컬럼명을 클릭하면 클릭할 때마다 오름차순/내림차순이 토글된다. 결제정보(은행명+
                계좌번호를 합쳐서 보여주는 파생값)는 정렬 대상에서 제외한다. */}
            <SortableTh label="구분" sortKey="entity_type" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="회사명" sortKey="company_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={{ textAlign:'left' }} />
            <SortableTh label="대표자" sortKey="ceo_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="사업자등록번호" sortKey="biz_reg_no" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="주소" sortKey="biz_address" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={{ textAlign:'left' }} />
            <SortableTh label="업태" sortKey="biz_type" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="업종" sortKey="biz_item" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <span style={{ textAlign:'left' }}>결제정보</span>
            <SortableTh label="담당자" sortKey="manager_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="담당자 연락처" sortKey="manager_contact" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="등록일" sortKey="created_at" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
          </div>
          {pagedList.map((c, i) => {
            const eStyle = entityStyleOf(c.entity_type);
            const rowChecked = selectedIds.has(c.id);
            return (
              <div key={c.id} onClick={() => selectMode ? toggleRowSelected(c.id) : setSelected(c)}
                style={{ display:'grid', gridTemplateColumns: selectMode ? '36px 90px 1fr 110px 150px 350px 90px 90px 210px 100px 130px 110px' : '90px 1fr 110px 150px 350px 90px 90px 210px 100px 130px 110px',
                  padding:'10px 10px', fontSize:14, textAlign:'center', alignItems:'center',
                  borderBottom: i < pagedList.length - 1 ? '1px solid #f0f0f0' : 'none',
                  background: rowChecked ? '#EFF6FF' : undefined,
                  cursor:'pointer' }}
                onMouseEnter={e => { if (!rowChecked) e.currentTarget.style.background = '#fafafa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = rowChecked ? '#EFF6FF' : '#fff'; }}>
                {selectMode && (
                  <span>
                    <input type="checkbox" checked={rowChecked} onChange={() => toggleRowSelected(c.id)}
                      onClick={e => e.stopPropagation()} style={{ cursor:'pointer' }} />
                  </span>
                )}
                <span>
                  {c.entity_type && (
                    <span style={{ fontSize:13, padding:'3px 10px', borderRadius:20, fontWeight:600, background:eStyle.bg, color:eStyle.color }}>
                      {c.entity_type}
                    </span>
                  )}
                </span>
                <span style={{ textAlign:'left', color:'#1a1a1a', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.company_name}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.ceo_name || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.biz_reg_no || '-'}</span>
                <span title={c.biz_address || ''} style={{ textAlign:'left', color:'#666666', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.biz_address || '-'}</span>
                <span style={{ color:'#666666', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.biz_type || '-'}</span>
                <span style={{ color:'#666666', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.biz_item || '-'}</span>
                <span title={(c.bank_name || c.account_number) ? `${c.bank_name || '-'} ㅣ ${c.account_number || '-'}` : ''}
                  style={{ textAlign:'left', color:'#666666', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {(c.bank_name || c.account_number)
                    ? `${c.bank_name || '-'} ㅣ ${c.account_number || '-'}`
                    : '-'}
                </span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.manager_name || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.manager_contact || '-'}</span>
                <span style={{ color:'#666666', fontSize:14 }}>{c.created_at?.slice(0, 10) || '-'}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* 페이지네이션 */}
      {pageSize !== 'all' && totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:14 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === 1 ? '#ccc' : '#555', fontSize:14, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>◀</button>
          <span style={{ fontSize:14, color:'#666' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page === totalPages ? '#ccc' : '#555', fontSize:14, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>▶</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (() => {
        const style = styleOf(selected.company_category);
        const canEditSelected   = canWrite;
        const canDeleteSelected = canDelete;
        return (
          <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
            onClick={() => setSelected(null)}>
            <div style={{ background:'#fff', borderRadius:16, padding:28, width:520, maxWidth:'90vw', maxHeight:'85vh', overflowY:'auto' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                <span style={{ display:'flex', gap:6 }}>
                  <span style={{ fontSize:13, padding:'4px 12px', borderRadius:20, fontWeight:600, background:style.bg, color:style.color }}>
                    {selected.company_category}
                  </span>
                  {selected.entity_type && (
                    <span style={{ fontSize:13, padding:'4px 12px', borderRadius:20, fontWeight:600, background:entityStyleOf(selected.entity_type).bg, color:entityStyleOf(selected.entity_type).color }}>
                      {selected.entity_type}
                    </span>
                  )}
                </span>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  {canWrite && (
                    <div style={{ display:'flex', gap:4 }}>
                      {CATEGORIES.map(cat => {
                        const active = selected.company_category === cat;
                        const s = styleOf(cat);
                        return (
                          <button key={cat} onClick={() => handleReclassify(selected, cat)} disabled={active}
                            style={{ fontSize:12, padding:'5px 10px', borderRadius:6, fontWeight:600,
                              border: `1px solid ${active ? '#ddd' : s.color}`,
                              background: active ? '#f5f5f5' : '#fff', color: active ? '#aaa' : s.color,
                              cursor: active ? 'default' : 'pointer' }}>
                            {cat}로 분류
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
                </div>
              </div>
              <h3 style={{ fontSize:17, fontWeight:600, margin:'0 0 16px' }}>{selected.company_name}</h3>

              {[
                ['대표자', selected.ceo_name],
                ['사업자등록번호', selected.biz_reg_no],
                ['사업장소재지', selected.biz_address],
                ['업태', selected.biz_type],
                ['종목', selected.biz_item],
                ['담당자', selected.manager_name],
                ['담당자 연락처', selected.manager_contact],
                ['담당자 이메일', selected.manager_email],
                ['은행명', selected.bank_name],
                ['예금주', selected.account_holder],
                ['계좌번호', selected.account_number],
                ['비고', selected.note],
                ['등록자', selected.created_by],
                ['등록일', selected.created_at?.slice(0, 10)],
              ].map(([label, val]) => val ? (
                <div key={label} style={{ display:'flex', gap:12, marginBottom:10, fontSize:14 }}>
                  <span style={{ width:100, color:'#888', flexShrink:0 }}>{label}</span>
                  <span style={{ color:'#1a1a1a' }}>{val}</span>
                </div>
              ) : null)}

              {canDownload && (selected.biz_cert_file_name || selected.bankbook_file_name) && (
                <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:8 }}>
                  {selected.biz_cert_file_name && (
                    <div style={{ padding:'12px 14px', background:'#f0f6ff', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:13, color:'#185FA5' }}>📎 사업자등록증 - {selected.biz_cert_file_ori}</span>
                      <button onClick={() => downloadCompanyFile(selected.biz_cert_file_name, selected.biz_cert_file_ori, () => setMsg('다운로드 실패'))}
                        style={{ background:'#185FA5', color:'#fff', border:'none', padding:'6px 14px', borderRadius:6, fontSize:13, cursor:'pointer' }}>
                        다운로드
                      </button>
                    </div>
                  )}
                  {selected.bankbook_file_name && (
                    <div style={{ padding:'12px 14px', background:'#f0f6ff', borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:13, color:'#185FA5' }}>📎 통장사본 - {selected.bankbook_file_ori}</span>
                      <button onClick={() => downloadCompanyFile(selected.bankbook_file_name, selected.bankbook_file_ori, () => setMsg('다운로드 실패'))}
                        style={{ background:'#185FA5', color:'#fff', border:'none', padding:'6px 14px', borderRadius:6, fontSize:13, cursor:'pointer' }}>
                        다운로드
                      </button>
                    </div>
                  )}
                </div>
              )}

              {(canEditSelected || canDeleteSelected) && (
                <div style={{ display:'flex', gap:8, marginTop:20 }}>
                  {canEditSelected && (
                    <button onClick={() => handleEdit(selected)}
                      style={{ flex:1, padding:'10px', background:'#f0f6ff', color:'#185FA5', border:'1px solid #185FA5', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
                      ✏️ 수정
                    </button>
                  )}
                  {canDeleteSelected && (
                    <button onClick={() => handleDelete(selected.id)}
                      style={{ flex:1, padding:'10px', background:'#fee2e2', color:'#dc2626', border:'1px solid #dc2626', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
                      🗑 삭제
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 업체등록(등록/수정) 모달 */}
      {showForm && (
        <CompanyForm
          editData={editData}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={() => { setShowForm(false); setEditData(null); load(); setMsg(editData ? '수정 완료!' : '등록 완료!'); }}
        />
      )}

      {/* 자동등록(스토리지 폴더 선택 → OCR 일괄등록) 모달 */}
      {showAutoRegister && (
        <AutoRegisterModal
          onClose={() => setShowAutoRegister(false)}
          onDone={(summary) => {
            load();
            const parts = [`등록 ${summary.registered.length}건`];
            if (summary.skipped.length) parts.push(`건너뜀 ${summary.skipped.length}건`);
            setMsg(`자동등록 완료 — ${parts.join(', ')}`);
          }}
        />
      )}
    </div>
  );
}

// ── 엑셀 업로드 자동등록용 원본 폴더 선택 미니 브라우저 ───────────────────────
// 사업자등록증/통장사본 원본이 회사별 하위 폴더로 정리된 스토리지 폴더를 지정할 때 쓴다.
// "폴더 선택" 버튼을 누르면 아코디언 형태로 트리가 펼쳐지고, 폴더를 고르면 그 경로가
// 값으로 저장된다(선택하지 않으면 첨부 자동연결 없이 엑셀의 텍스트 항목만 등록된다).
function MiniFolderPicker({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [curPath, setCurPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFolder = (p) => {
    setLoading(true);
    API.get('/storage/list', { params: { path: p } })
      .then(r => { setItems((r.data.items || []).filter(it => it.isDir)); setCurPath(p); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  const toggleOpen = () => {
    if (!open) loadFolder(value || '');
    setOpen(o => !o);
  };
  const crumbs = curPath ? curPath.split(/[\\/]/).filter(Boolean) : [];

  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
        <span style={{ fontSize:13, color:'#666', minWidth:132 }}>{label}</span>
        <span style={{ flex:1, fontSize:13, color: value ? '#185FA5' : '#aaa', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {value ? `📁 ${value}` : '선택 안 함(첨부 자동연결 생략)'}
        </span>
        {value && <span onClick={() => onChange('')} style={{ fontSize:12, color:'#dc2626', cursor:'pointer' }}>지우기</span>}
        <button type="button" onClick={toggleOpen}
          style={{ padding:'4px 10px', fontSize:12, border:'1px solid #e5e7eb', borderRadius:6, background:'#fff', cursor:'pointer' }}>
          {open ? '닫기' : '폴더 선택'}
        </button>
      </div>
      {open && (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:10, background:'#fafafa' }}>
          <div style={{ fontSize:12, color:'#185FA5', marginBottom:6, display:'flex', flexWrap:'wrap', gap:4, alignItems:'center' }}>
            <span onClick={() => loadFolder('')} style={{ cursor:'pointer', fontWeight: curPath === '' ? 700 : 500 }}>📁 스토리지</span>
            {crumbs.map((c, i) => {
              const p = crumbs.slice(0, i + 1).join('/');
              return (
                <span key={p} style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ color:'#ccc' }}>/</span>
                  <span onClick={() => loadFolder(p)} style={{ cursor:'pointer', fontWeight: i === crumbs.length - 1 ? 700 : 500 }}>{c}</span>
                </span>
              );
            })}
          </div>
          <div style={{ maxHeight:140, overflowY:'auto', marginBottom:8, background:'#fff', border:'1px solid #f0f0f0', borderRadius:6 }}>
            {loading ? (
              <div style={{ padding:10, textAlign:'center', color:'#aaa', fontSize:12 }}>불러오는 중...</div>
            ) : items.length === 0 ? (
              <div style={{ padding:10, textAlign:'center', color:'#aaa', fontSize:12 }}>하위 폴더가 없습니다.</div>
            ) : items.map(it => (
              <div key={it.name} onClick={() => loadFolder(curPath ? `${curPath}/${it.name}` : it.name)}
                style={{ padding:'6px 10px', fontSize:13, cursor:'pointer', borderBottom:'1px solid #f5f5f5' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                📁 {it.name}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => { onChange(curPath); setOpen(false); }}
            style={{ width:'100%', padding:'6px', fontSize:12, background:'#185FA5', color:'#fff', border:'none', borderRadius:6, cursor:'pointer' }}>
            "{curPath || '스토리지 최상위'}" 폴더로 선택
          </button>
        </div>
      )}
    </div>
  );
}

// ── 엑셀 업로드 자동등록용 사업자등록증/통장사본 직접 업로드 드롭존 ─────────────────
// 스토리지에 파일이 정리돼 있지 않고, 지금 컴퓨터에서 실제 사업자등록증/통장사본 파일을
// 바로 첨부하려는 경우에 쓴다. 여러 회사 분량의 파일을 한 번에 여러 개 선택/드롭할 수
// 있고, 서버가 각 파일명에 포함된 회사명으로 엑셀의 어느 행(회사)에 속하는지 자동으로
// 판별한다(파일명에 회사명이 포함되어 있어야 매칭된다).
function MultiFileDropZone({ label, files, onFiles }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (arr.length) onFiles([...files, ...arr]);
  };
  const removeAt = (i) => onFiles(files.filter((_, idx) => idx !== i));

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:13, color:'#666', marginBottom:4 }}>{label}</div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        style={{ border: `1px dashed ${dragOver ? '#185FA5' : '#ccc'}`, borderRadius:8, padding:'14px',
          textAlign:'center', cursor:'pointer', fontSize:13, color:'#888',
          background: dragOver ? '#EAF3FC' : '#fafafa' }}>
        📎 사업자등록증·통장사본 파일을 여기로 끌어놓거나 클릭하여 선택하세요 (여러 개 선택 가능)
        <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {files.length > 0 && (
        <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:4 }}>
          {files.map((f, i) => (
            <div key={`${f.name}_${i}`} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              fontSize:12, color:'#555', background:'#f0f6ff', borderRadius:6, padding:'5px 10px' }}>
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📄 {f.name}</span>
              <span onClick={() => removeAt(i)} style={{ color:'#dc2626', cursor:'pointer', marginLeft:8, flexShrink:0 }}>지우기</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 자동등록 모달 ────────────────────────────────────────────
// 스토리지(D:\storage) 폴더 트리를 간단히 탐색해서 폴더를 하나 선택하면, 그 폴더(하위폴더가
// 있으면 하위폴더 각각)를 업체 1곳으로 보고 OCR로 자동등록한다. 자동등록은 회사가 여러
// 개(수십 개)일 수 있고 OCR도 파일당 수 초가 걸릴 수 있어 완료까지 시간이 걸릴 수 있다.
function AutoRegisterModal({ onClose, onDone }) {
  const [mode, setMode] = useState('folder'); // 'folder' | 'excel'
  const [curPath, setCurPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  // 2026-08 수정 — 사업자등록증/통장사본 폴더를 따로 지정하지 않고 하나로 통합했다(둘이
  // 한 폴더에 같이 있는 경우가 많아서). docFiles는 스토리지에 없을 때 실제 파일을 바로
  // 첨부하는 세 번째 방식.
  const [docFolder, setDocFolder] = useState('');
  const [docFiles, setDocFiles] = useState([]);

  const loadFolder = (p) => {
    setLoading(true);
    setError('');
    API.get('/storage/list', { params: { path: p } })
      .then(r => { setItems((r.data.items || []).filter(it => it.isDir)); setCurPath(p); })
      .catch(err => setError(err.response?.data?.message || '폴더를 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (mode === 'folder') loadFolder(''); }, [mode]); // eslint-disable-line

  const crumbs = curPath ? curPath.split(/[\\/]/).filter(Boolean) : [];

  const handleStart = () => {
    if (!window.confirm(`"${curPath || '스토리지 최상위'}" 폴더 안의 업체를 자동으로 등록하시겠습니까?\n(파일 수에 따라 다소 시간이 걸릴 수 있습니다.)`)) return;
    setRunning(true);
    setError('');
    API.post('/companies/auto-register', { path: curPath }, { timeout: 10 * 60 * 1000 })
      .then(r => setResult(r.data))
      .catch(err => setError(err.response?.data?.message || '자동등록 중 오류가 발생했습니다.'))
      .finally(() => setRunning(false));
  };

  const handleStartExcel = () => {
    if (!excelFile) return;
    if (!window.confirm(`"${excelFile.name}" 파일의 업체 목록을 등록하시겠습니까?`)) return;
    setRunning(true);
    setError('');
    const fd = new FormData();
    fd.append('file', excelFile);
    if (docFolder) fd.append('docFolder', docFolder);
    docFiles.forEach(f => fd.append('docFiles', f));
    API.post('/companies/import-excel', fd, { timeout: 5 * 60 * 1000 })
      .then(r => setResult(r.data))
      .catch(err => setError(err.response?.data?.message || '엑셀 가져오기 중 오류가 발생했습니다.'))
      .finally(() => setRunning(false));
  };

  // 엑셀 작성 양식 다운로드 — 백엔드 IMPORT_HEADER_MAP이 인식하는 열 제목 그대로 헤더를
  // 만들고, 예시행 1개를 넣어준다(사용자가 지우고 실제 데이터를 채우도록 안내).
  const handleDownloadImportTemplate = () => {
    const headers = ['구분','사업자구분','회사명','대표자','사업자등록번호','사업장소재지','업태','종목','담당자','담당자연락처','담당자이메일','은행명','예금주','계좌번호','비고'];
    const example = ['고객사','법인','예시상사','홍길동','123-45-67890','서울시 강남구 ○○로 10','도소매','전자제품','김담당','010-1234-5678','manager@example.com','국민은행','예시상사','123456-78-901234','예시 행입니다. 실제 등록 전 이 행은 삭제하세요.'];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    ws['!cols'] = [{wch:8},{wch:10},{wch:18},{wch:10},{wch:16},{wch:26},{wch:12},{wch:12},{wch:10},{wch:14},{wch:20},{wch:12},{wch:12},{wch:18},{wch:34}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '업체목록');
    XLSX.writeFile(wb, '업체관리_엑셀등록_양식.xlsx');
  };

  const handleFinish = () => {
    if (result) onDone(result);
    onClose();
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:520, maxWidth:'90vw', maxHeight:'85vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🤖 업체 자동등록</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {!result && (
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <button onClick={() => setMode('folder')}
              style={{ flex:1, padding:'8px 0', borderRadius:8, border: mode === 'folder' ? '1px solid #185FA5' : '1px solid #e5e7eb',
                background: mode === 'folder' ? '#EAF3FC' : '#fff', color: mode === 'folder' ? '#185FA5' : '#666',
                fontSize:13, fontWeight:600, cursor:'pointer' }}>
              📁 폴더 선택
            </button>
            <button onClick={() => setMode('excel')}
              style={{ flex:1, padding:'8px 0', borderRadius:8, border: mode === 'excel' ? '1px solid #185FA5' : '1px solid #e5e7eb',
                background: mode === 'excel' ? '#EAF3FC' : '#fff', color: mode === 'excel' ? '#185FA5' : '#666',
                fontSize:13, fontWeight:600, cursor:'pointer' }}>
              📊 엑셀 업로드
            </button>
          </div>
        )}

        {!result && mode === 'folder' && (
          <>
            <p style={{ fontSize:13, color:'#666', margin:'0 0 14px', lineHeight:1.5 }}>
              스토리지에서 폴더를 선택하세요. 선택한 폴더 안에 하위 폴더가 있으면 하위 폴더마다
              업체 1곳으로, 없으면 선택한 폴더 자체를 업체 1곳으로 보고 사업자등록증/통장사본을
              찾아 자동으로 등록합니다.
            </p>

            <div style={{ fontSize:13, color:'#185FA5', marginBottom:8, display:'flex', flexWrap:'wrap', gap:4, alignItems:'center' }}>
              <span onClick={() => loadFolder('')} style={{ cursor:'pointer', fontWeight: curPath === '' ? 700 : 500 }}>📁 스토리지</span>
              {crumbs.map((c, i) => {
                const p = crumbs.slice(0, i + 1).join('/');
                return (
                  <span key={p} style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <span style={{ color:'#ccc' }}>/</span>
                    <span onClick={() => loadFolder(p)} style={{ cursor:'pointer', fontWeight: i === crumbs.length - 1 ? 700 : 500 }}>{c}</span>
                  </span>
                );
              })}
            </div>

            <div style={{ border:'1px solid #e5e7eb', borderRadius:8, minHeight:160, maxHeight:280, overflowY:'auto', marginBottom:14 }}>
              {loading ? (
                <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>불러오는 중...</div>
              ) : items.length === 0 ? (
                <div style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>하위 폴더가 없습니다.</div>
              ) : items.map(it => (
                <div key={it.name} onClick={() => loadFolder(curPath ? `${curPath}/${it.name}` : it.name)}
                  style={{ padding:'10px 14px', fontSize:14, cursor:'pointer', borderBottom:'1px solid #f0f0f0' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  📁 {it.name}
                </div>
              ))}
            </div>

            {error && <div style={{ fontSize:13, color:'#dc2626', marginBottom:12 }}>{error}</div>}
            {running && <div style={{ fontSize:13, color:'#185FA5', marginBottom:12 }}>⏳ 인식 및 등록 중입니다. 폴더 수가 많으면 시간이 걸릴 수 있습니다...</div>}

            <button onClick={handleStart} disabled={running}
              style={{ width:'100%', padding:'12px', background: running ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
                fontSize:15, fontWeight:600, cursor: running ? 'not-allowed' : 'pointer' }}>
              {running ? '등록 중...' : `📥 "${curPath || '스토리지 최상위'}" 폴더로 자동등록 시작`}
            </button>
          </>
        )}

        {!result && mode === 'excel' && (
          <>
            <p style={{ fontSize:13, color:'#666', margin:'0 0 14px', lineHeight:1.5 }}>
              업체 정보(회사명/사업자등록번호/대표자/주소/은행정보 등)가 열별로 정리된 엑셀 파일을
              업로드하세요. 1행은 열 제목(회사명, 사업자등록번호, 대표자, 구분 등)으로 인식하고,
              2행부터 각 행을 업체 1곳으로 등록합니다. 사업자구분(법인/개인 등) 열이 비어있으면
              회사명 문구로 자동 분류합니다.
            </p>
            <p style={{ fontSize:13, color:'#666', margin:'0 0 14px', lineHeight:1.5 }}>
              사업자등록증·통장사본은 아래 세 가지 방식을 자유롭게(섞어서도) 사용할 수 있습니다 —
              ① 스토리지 폴더 안에 회사명별 하위 폴더가 있고 그 안에 두 서류가 함께 있는 경우,
              ② 하위 폴더 구분 없이 파일명에 회사명이 포함된 채로 한 폴더에 섞여 있는 경우(①②
              모두 아래 폴더 지정 하나로 처리됩니다), ③ 스토리지에 없어 지금 컴퓨터에서 실제
              파일을 바로 첨부하는 경우(아래 직접 업로드). 어떤 파일이 사업자등록증이고 어떤
              파일이 통장사본인지는 파일명(예: "사업자등록증", "통장사본")으로 먼저 구분하고,
              구분이 안 되면 자동 인식(OCR)으로 판별합니다. 모두 선택 사항이며, 아무것도
              지정하지 않으면 텍스트 항목만 등록됩니다.
            </p>

            <button type="button" onClick={handleDownloadImportTemplate}
              style={{ width:'100%', padding:'9px', marginBottom:14, background:'#fff', color:'#185FA5', border:'1px solid #185FA5',
                borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              📥 엑셀 작성 양식 다운로드
            </button>

            <MiniFolderPicker label="사업자등록증·통장사본 폴더" value={docFolder} onChange={setDocFolder} />
            <MultiFileDropZone label="사업자등록증·통장사본 직접 업로드" files={docFiles} onFiles={setDocFiles} />

            <FileDropZone
              fileName={excelFile?.name}
              onFile={setExcelFile}
              accept=".xlsx,.xls"
              style={{ marginBottom:14 }}
            />

            {error && <div style={{ fontSize:13, color:'#dc2626', marginBottom:12 }}>{error}</div>}
            {running && <div style={{ fontSize:13, color:'#185FA5', marginBottom:12 }}>⏳ 엑셀을 분석해 등록 중입니다...</div>}

            <button onClick={handleStartExcel} disabled={running || !excelFile}
              style={{ width:'100%', padding:'12px', background: (running || !excelFile) ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
                fontSize:15, fontWeight:600, cursor: (running || !excelFile) ? 'not-allowed' : 'pointer' }}>
              {running ? '등록 중...' : '📊 엑셀로 자동등록 시작'}
            </button>
          </>
        )}

        {result && (
          <>
            <div style={{ fontSize:14, marginBottom:12 }}>
              총 {result.total}건 중 <b style={{ color:'#0F6E56' }}>등록 {result.registered.length}건</b>
              {result.skipped.length > 0 && <>, <b style={{ color:'#dc2626' }}>건너뜀 {result.skipped.length}건</b></>}
            </div>
            {result.registered.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#0F6E56', marginBottom:6 }}>✅ 등록됨</div>
                {result.registered.map(r => {
                  const attachParts = [
                    r.cert !== undefined ? `사업자등록증 ${r.cert ? '첨부됨' : '미매칭'}` : null,
                    r.bank !== undefined ? `통장사본 ${r.bank ? '첨부됨' : '미매칭'}` : null,
                  ].filter(Boolean);
                  return (
                    <div key={r.id} style={{ fontSize:13, color:'#333', padding:'4px 0' }}>
                      {r.name}
                      {attachParts.length > 0 && (
                        <span style={{ fontSize:12, color:'#999', marginLeft:6 }}>({attachParts.join(' / ')})</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#dc2626', marginBottom:6 }}>⚠️ 건너뜀</div>
                {result.skipped.map((r, i) => (
                  <div key={i} style={{ fontSize:13, color:'#666', padding:'4px 0' }}>
                    {r.name} — {r.reason}
                  </div>
                ))}
              </div>
            )}
            <button onClick={handleFinish}
              style={{ width:'100%', padding:'12px', background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                fontSize:15, fontWeight:600, cursor:'pointer' }}>
              확인
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── 업체등록(등록/수정) 폼 컴포넌트 ──────────────────────────
// 사업자등록증을 첨부하면 즉시 OCR(POST /companies/ocr/biz-cert)을 호출해 회사명/대표자/
// 사업자번호/주소/업태/종목을 자동으로 채워준다. 통장사본을 첨부하면 마찬가지로 은행명/예금주/
// 계좌번호를 자동 인식한다. OCR 인식률은 완벽하지 않으므로(특히 대표자명/계좌번호) 사용자가
// 반드시 검수 후 저장하도록 안내 문구를 노출한다. 이미 사용자가 입력해둔 값은 덮어쓰지 않는다.
function CompanyForm({ editData, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_category: editData?.company_category || CATEGORIES[0],
    entity_type:      editData?.entity_type      || '',
    company_name:     editData?.company_name     || '',
    ceo_name:         editData?.ceo_name         || '',
    biz_reg_no:       editData?.biz_reg_no       || '',
    biz_address:      editData?.biz_address      || '',
    biz_type:         editData?.biz_type         || '',
    biz_item:         editData?.biz_item         || '',
    manager_name:     editData?.manager_name     || '',
    manager_contact:  editData?.manager_contact  || '',
    manager_email:    editData?.manager_email    || '',
    bank_name:        editData?.bank_name        || '',
    account_holder:   editData?.account_holder   || '',
    account_number:   editData?.account_number   || '',
    note:             editData?.note             || '',
  });
  const [bizCertFile, setBizCertFile] = useState(null);
  const [bankbookFile, setBankbookFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(null); // 'cert' | 'bankbook' | null
  const [ocrNotice, setOcrNotice] = useState('');
  const [loading, setLoading] = useState(false);
  // 2026-08 추가 — OCR 교정 기록(등록/수정 시 방금 통장사본 자동인식이 준 값과 실제 저장값을
  // 비교해 어긋난 항목만 서버가 기록, 이후 인식 정확도 향상에 예시로 재사용). 통장사본
  // OCR 미리보기(/companies/ocr/bankbook) 응답에 담겨오는 토큰을 저장해 두었다가, 최종
  // 등록/수정 요청에 그대로 실어 보낸다 — 새 파일을 다시 업로드하면(=재인식) 새 토큰으로 교체.
  const [ocrGuessToken, setOcrGuessToken] = useState(null);
  // 2026-08 추가 — 사업자등록증도 통장사본과 동일한 방식으로 로컬 레퍼런스 저장소에 누적한다.
  const [ocrCertGuessToken, setOcrCertGuessToken] = useState(null);
  // 2026-09 추가 — 사업자등록증/통장사본 OCR 신뢰도 개편: 서버가 필드별 OCR 신뢰도
  // (0~100, 비전AI 출처 등 확인 불가 시 null)를 함께 내려주면 낮은 신뢰도 필드에
  // 붉은 테두리로 표시해 사용자가 다시 확인하도록 유도한다(Contacts.js와 동일 패턴).
  const [certConfidence, setCertConfidence] = useState(null);
  const [bankbookConfidence, setBankbookConfidence] = useState(null);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // 사용자가 아직 입력하지 않은 필드만 OCR 결과로 채운다(이미 입력된 값은 보존).
  // 2026-09(5차) 버그수정 — Settings.js(환경설정-회사정보)와 동일한 원인/동일한 수정.
  // "빈 칸만" 채우는 기존 로직 때문에 사업자등록증을 재업로드해도 이미 채워져 있던
  // biz_type/biz_item(과거 잘못 인식된 값 포함)이 새로 고친 인식 결과로 절대 덮어써지지
  // 않았다. forceKeys에 지정한 필드는 새로 인식된 값이 있으면 기존 값을 덮어쓴다.
  const mergeOcrFields = (fields, forceKeys = []) => {
    setForm(f => {
      const next = { ...f };
      Object.entries(fields).forEach(([k, v]) => {
        if (!v) return;
        if (next[k] && !forceKeys.includes(k)) return;
        next[k] = v;
      });
      return next;
    });
  };

  const handleBizCertChange = async (file) => {
    setBizCertFile(file || null);
    if (!file) return;
    setOcrLoading('cert');
    setOcrNotice('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await API.post('/companies/ocr/biz-cert', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.ok && data.fields) {
        mergeOcrFields({
          // 업체등록 모달의 회사명 입력칸에는 법인격(주식회사/㈜ 등)을 포함한 전체 상호가
          // 그대로 채워져야 하므로 company_name(법인격 제거본, 대량 자동등록 전용) 대신
          // company_name_raw를 사용한다. 값이 없는 예전 응답 대비 company_name으로 폴백.
          company_name: data.fields.company_name_raw || data.fields.company_name,
          entity_type:  data.fields.entity_type,
          ceo_name:     data.fields.ceo_name,
          biz_reg_no:   data.fields.biz_reg_no,
          biz_address:  data.fields.biz_address,
          biz_type:     data.fields.biz_type,
          biz_item:     data.fields.biz_item,
        }, ['biz_type', 'biz_item']);
        setOcrCertGuessToken(data.ocrCertGuessToken || null);
        setCertConfidence(data.fields.confidence || null);
        setOcrNotice('📋 사업자등록증에서 자동 인식된 정보로 채웠습니다. 정확하지 않을 수 있으니 반드시 확인 후 저장해주세요.');
      } else {
        setOcrNotice('사업자등록증 자동인식에 실패했습니다. 직접 입력해주세요.');
      }
    } catch (err) {
      setOcrNotice('사업자등록증 자동인식 중 오류가 발생했습니다. 직접 입력해주세요.');
    } finally {
      setOcrLoading(null);
    }
  };

  const handleBankbookChange = async (file) => {
    setBankbookFile(file || null);
    if (!file) return;
    setOcrLoading('bankbook');
    setOcrNotice('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await API.post('/companies/ocr/bankbook', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.ok && data.fields) {
        mergeOcrFields({
          bank_name:      data.fields.bank_name,
          account_holder: data.fields.account_holder,
          account_number: data.fields.account_number,
        });
        setOcrGuessToken(data.ocrGuessToken || null);
        setBankbookConfidence(data.fields.confidence || null);
        setOcrNotice('📋 통장사본에서 자동 인식된 정보로 채웠습니다. 정확하지 않을 수 있으니 반드시 확인 후 저장해주세요.');
      } else {
        setOcrNotice('통장사본 자동인식에 실패했습니다. 직접 입력해주세요.');
      }
    } catch (err) {
      setOcrNotice('통장사본 자동인식 중 오류가 발생했습니다. 직접 입력해주세요.');
    } finally {
      setOcrLoading(null);
    }
  };

  const handleSubmit = async () => {
    if (!form.company_category) return alert('구분(고객사/협력사)을 선택해주세요.');
    if (!form.company_name) return alert('회사명을 입력해주세요.');
    setLoading(true);

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (bizCertFile) fd.append('file', bizCertFile);
    if (bankbookFile) fd.append('file2', bankbookFile);
    // OCR 교정 기록용 — 통장사본 미리보기 때 받은 토큰을 그대로 실어 보내면, 서버가 방금
    // AI가 읽었던 값과 지금 실제로 저장하는 값을 비교해 교정기록을 남긴다(없으면 그냥 생략).
    if (ocrGuessToken) fd.append('ocrGuessToken', ocrGuessToken);
    if (ocrCertGuessToken) fd.append('ocrCertGuessToken', ocrCertGuessToken);

    try {
      if (editData) {
        await API.put(`/companies/${editData.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await API.post('/companies', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const labelStyle = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };
  // 2026-09 추가 — Contacts.js와 동일한 신뢰도 표시 규칙: 값이 비어있으면 표시하지 않고,
  // 신뢰도가 숫자가 아니면(비전AI 출처 등 확인불가 = null) 중립으로 둔다. 숫자이면서
  // 임계값(60) 미만일 때만 붉은 테두리로 재확인을 유도한다.
  const certStyle = (key) => {
    const c = certConfidence && certConfidence[key];
    if (!form[key] || typeof c !== 'number' || c >= 60) return inputStyle;
    return { ...inputStyle, border: '1.5px solid #ef4444', background: '#fff5f5' };
  };
  const bankStyle = (key) => {
    const c = bankbookConfidence && bankbookConfidence[key];
    if (!form[key] || typeof c !== 'number' || c >= 60) return inputStyle;
    return { ...inputStyle, border: '1.5px solid #ef4444', background: '#fff5f5' };
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:560, maxWidth:'90vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{editData ? '업체 수정' : '업체등록'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {ocrNotice && (
          <div style={{ fontSize:12, color:'#854F0B', background:'#FAEEDA', padding:'8px 12px', borderRadius:8, marginBottom:16 }}>
            {ocrNotice}
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>구분 *</label>
            <select name="company_category" value={form.company_category} onChange={handleChange} style={inputStyle}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>회사명 *</label>
            <input name="company_name" placeholder="회사명" value={form.company_name} onChange={handleChange} style={certStyle('company_name')} />
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>사업자구분</label>
            <select name="entity_type" value={form.entity_type} onChange={handleChange} style={inputStyle}>
              <option value="">선택 안함</option>
              {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>대표자</label>
            <input name="ceo_name" placeholder="대표자명" value={form.ceo_name} onChange={handleChange} style={certStyle('ceo_name')} />
          </div>
          <div>
            <label style={labelStyle}>사업자등록번호</label>
            <input name="biz_reg_no" placeholder="000-00-00000" value={form.biz_reg_no} onChange={handleChange} style={certStyle('biz_reg_no')} />
          </div>
        </div>

        <label style={labelStyle}>사업장소재지</label>
        <input name="biz_address" placeholder="사업장 주소" value={form.biz_address} onChange={handleChange} style={certStyle('biz_address')} />

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>업태</label>
            <input name="biz_type" placeholder="업태" value={form.biz_type} onChange={handleChange} style={certStyle('biz_type')} />
          </div>
          <div>
            <label style={labelStyle}>종목</label>
            <input name="biz_item" placeholder="종목" value={form.biz_item} onChange={handleChange} style={certStyle('biz_item')} />
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>담당자</label>
            <input name="manager_name" placeholder="담당자명" value={form.manager_name} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>담당자 연락처</label>
            <input name="manager_contact" placeholder="010-0000-0000" value={form.manager_contact} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>담당자 이메일</label>
            <input name="manager_email" placeholder="email@company.com" value={form.manager_email} onChange={handleChange} style={inputStyle} />
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
          <div>
            <label style={labelStyle}>은행명</label>
            <input name="bank_name" placeholder="은행명" value={form.bank_name} onChange={handleChange} style={bankStyle('bank_name')} />
          </div>
          <div>
            <label style={labelStyle}>예금주</label>
            <input name="account_holder" placeholder="예금주" value={form.account_holder} onChange={handleChange} style={bankStyle('account_holder')} />
          </div>
          <div>
            <label style={labelStyle}>계좌번호</label>
            <input name="account_number" placeholder="계좌번호" value={form.account_number} onChange={handleChange} style={bankStyle('account_number')} />
          </div>
        </div>

        <label style={labelStyle}>비고</label>
        <textarea name="note" placeholder="비고" value={form.note} onChange={handleChange}
          style={{ ...inputStyle, minHeight:60, resize:'vertical' }} />

        {/* 첨부파일 — 사업자등록증/통장사본, 인증관리(Certs.js) 드롭존 방식으로 한 줄에 배치.
            사업자등록증 첨부 시 OCR 자동인식(회사명/대표자/사업자번호/주소/업태/종목/사업자구분)이 실행된다. */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:4 }}>
          <div>
            <label style={labelStyle}>사업자등록증 첨부{ocrLoading === 'cert' && <span style={{ color:'#185FA5', fontSize:12 }}> · 🔎 인식 중...</span>}</label>
            <FileDropZone
              currentLabel={editData?.biz_cert_file_ori}
              fileName={bizCertFile?.name}
              onFile={handleBizCertChange}
            />
          </div>
          <div>
            <label style={labelStyle}>통장사본 첨부{ocrLoading === 'bankbook' && <span style={{ color:'#185FA5', fontSize:12 }}> · 🔎 인식 중...</span>}</label>
            <FileDropZone
              currentLabel={editData?.bankbook_file_ori}
              fileName={bankbookFile?.name}
              onFile={handleBankbookChange}
            />
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', marginTop:8,
            background: loading ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
            fontSize:15, fontWeight:600, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '업체등록')}
        </button>
      </div>
    </div>
  );
}

